import React, { useState } from "react";
import { motion } from "framer-motion";
import { fadeUp } from "../../animations/variants";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AddressText } from "i18n/contactCTA";

import {
  MapPin,
  Phone,
  Mail,
  Clock3,
  Building2,
  ArrowRight,
} from "lucide-react";

const Address = () => {
  const { sourceSlug, sourceTitle } = useSelector((state) => state.contact);

  const [searchParams] = useSearchParams();

  const workshopSlug = searchParams.get("workshop");
  const workshopTitle = searchParams.get("title");

  const lang = useSelector((state) => state.language.lang);
  const t = AddressText[lang];
  const [showForm, setShowForm] = useState(false);

  const iconMap = {
    office: Building2,
    phone: Phone,
    email: Mail,
    hours: Clock3,
  };

  const navigate = useNavigate();
  // Prefer URL → fallback Redux
  const finalTitle = workshopSlug
    ? workshopTitle || workshopSlug.replaceAll("-", " ")
    : "";

  return (
    <section className=" py-24">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-10 items-start">
        {/* LEFT SIDE */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="space-y-10"
        >
          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <p className="uppercase tracking-[0.3em] text-xs text-[#9B6B53] mb-3">
              Maria Advisory
            </p>

            <h2 className="font-[Crimson] text-4xl md:text-6xl text-reddark mb-4 ">
              {t.title}
            </h2>

            <div className="w-24 h-[2px] bg-reddark mt-5" />
          </motion.div>

          {/* Contact Cards */}
          <div className="grid gap-5">
            {t.contactCards.map((item, index) => {
              const Icon = iconMap[item.type] || MapPin;

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.12,
                  }}
                  whileHover={{
                    y: -6,
                  }}
                  className="
            group
            relative
            overflow-hidden
            rounded-3xl
            border-0 border-[#E8DED5]
            hover:bg-white
            p-2
            hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)]
            transition-all duration-500
          "
                >
                  {/* Hover Accent */}
                  <div
                    className="
              absolute
              left-0
              top-0
              h-full
              w-1
              bg-reddark
              scale-y-0
              origin-top
              group-hover:scale-y-100
              transition-transform
              duration-500
            "
                  />

                  <div className="flex gap-5">
                    <motion.div
                      whileHover={{
                        rotate: 6,
                        scale: 1.08,
                      }}
                      className="
                w-14 h-14
                rounded-2xl
                bg-reddark/10
                flex items-center justify-center
                shrink-0
                group-hover:bg-reddark
                transition-all duration-500
              "
                    >
                      <Icon
                        size={24}
                        className="
                  text-reddark
                  group-hover:text-white
                  transition-all duration-500
                "
                      />
                    </motion.div>

                    <div className="flex-1">
                      <p className="text-xs uppercase tracking-[0.25em] text-[#9B6B53] mb-2">
                        {item.title}
                      </p>

                      {item.type === "phone" ? (
                        <>
                          <a
                            href="tel:+64210746258"
                            className="
                      text-sm
                      font-medium
                      text-[#111]
                      hover:text-reddark
                      transition
                    "
                          >
                            {item.value}
                          </a>

                          {item.value2 && (
                            <p className="text-sm text-gray-500 mt-2">
                              {item.value2}
                            </p>
                          )}
                        </>
                      ) : item.type === "email" ? (
                        <a
                          href={`mailto:${item.value}`}
                          className="
                    text-sm
                    font-medium
                    text-[#111]
                    hover:text-reddark
                    transition
                  "
                        >
                          {item.value}
                        </a>
                      ) : (
                        <>
                          <p className="text-sm font-medium text-[#111] leading-relaxed">
                            {item.value}
                          </p>

                          {item.value2 && (
                            <p className="text-gray-500 mt-2 text-sm">
                              {item.value2}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* RIGHT SIDE FORM */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          whileHover={{ y: -5 }}
          className="
    relative
    overflow-hidden
    rounded-[32px]
    border border-[#E8DED5]
    bg-white
    shadow-[0_20px_60px_rgba(0,0,0,0.12)]
    min-h-[600px]
  "
        >
          <iframe
            title="Maria Advisory Map"
            src="https://www.google.com/maps?q=36+Princes+Street+Onehunga+Auckland&output=embed"
            className="absolute inset-0 w-full h-full border-0"
            loading="lazy"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

          <div className="absolute bottom-8 left-8 right-8">
            <a
              href="https://maps.google.com/?q=36+Princes+Street+Onehunga+Auckland"
              target="_blank"
              rel="noreferrer"
              className="
        w-full
        flex items-center justify-center gap-3
        bg-reddark
        text-white
        py-4
        rounded-2xl
        font-semibold
        hover:bg-black
        transition-all
      "
            >
              Get Directions
              <ArrowRight size={18} />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Address;
